import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Loader } from './components/UI';
import Layout from './components/Layout/Layout';
import { Header, StatusBar } from './components/Layout';

// Lazy load pages to improve initial load performance
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const MapPageWrapper = lazy(() => import('./components/Layout/MapPageWrapper'));
const Profile = lazy(() => import('./pages/Profile'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));

// Protected route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader size="lg" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Layout for public pages
const PublicLayout = ({ children }) => {
  return (
    <div className="flex flex-col min-h-screen">
      <div className="app-header">
        <Header />
      </div>
      <main className="flex-1 main-content" style={{ overflow: 'visible' }}>
        {children}
      </main>
      <div
        className="status-bar-container"
        style={{ position: 'relative', bottom: 'auto', marginTop: 'auto' }}
      >
        <StatusBar />
      </div>
    </div>
  );
};

const AppRoutes = () => {
  const { isAuthenticated } = useAuth();

  return (
    <Suspense 
      fallback={
        <div className="flex justify-center items-center h-screen">
          <Loader size="lg" />
        </div>
      }
    >
      <Routes>
        {/* Root route - always open maps to public */}
        <Route 
          path="/" 
          element={<Navigate to="/maps" replace />} 
        />

        {/* Public routes */}
        <Route path="/login" element={<PublicLayout><Login /></PublicLayout>} />
        <Route path="/register" element={<PublicLayout><Register /></PublicLayout>} />
        <Route path="/privacy-policy" element={<PublicLayout><PrivacyPolicy /></PublicLayout>} />
        
        {/* Protected routes - now using the new Layout component */}
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <Layout>
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/maps" 
          element={
            <Layout>
              <MapPageWrapper />
            </Layout>
          } 
        />
        <Route 
          path="/profile" 
          element={
            <ProtectedRoute>
              <Layout>
                <Profile />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/shapefile" 
          element={
            <ProtectedRoute>
              <Layout>
                <div>Shapefile Upload Page</div>
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        {/* Fallback route - guests should land on public maps */}
        <Route path="*" element={
          isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/maps" replace />
        } />
      </Routes>
    </Suspense>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
}

export default App;


